/* ============================================================
   BB BY LYRAH — Product Page
   Gallery slider · Variant picker · Ajax cart
   ============================================================ */

(function () {
  'use strict';

  /* ── GALLERY ── */
  const Gallery = {
    track: null,
    slides: [],
    thumbs: [],
    dots: [],
    current: 0,
    startX: 0,
    isDragging: false,

    init() {
      this.track = document.getElementById('bb-gallery-track');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('.bb-gallery__slide'));
      this.thumbs = Array.from(document.querySelectorAll('.bb-gallery-thumbs__btn'));
      this.dots   = Array.from(document.querySelectorAll('.bb-gallery__dot'));

      document.getElementById('bb-gallery-prev')
        ?.addEventListener('click', () => this.go(this.current - 1));
      document.getElementById('bb-gallery-next')
        ?.addEventListener('click', () => this.go(this.current + 1));

      this.thumbs.forEach(btn => {
        btn.addEventListener('click', () => this.go(parseInt(btn.dataset.index, 10)));
      });

      /* Touch / drag swipe */
      this.track.addEventListener('touchstart',  e => this.onTouchStart(e), { passive: true });
      this.track.addEventListener('touchend',    e => this.onTouchEnd(e),   { passive: true });
      this.track.addEventListener('mousedown',   e => this.onMouseDown(e));
      this.track.addEventListener('mouseup',     e => this.onMouseUp(e));
      this.track.addEventListener('mouseleave',  () => { this.isDragging = false; });

      /* Keyboard on gallery container */
      document.getElementById('bb-gallery')
        ?.addEventListener('keydown', e => {
          if (e.key === 'ArrowLeft')  this.go(this.current - 1);
          if (e.key === 'ArrowRight') this.go(this.current + 1);
        });
    },

    go(index) {
      const count = this.slides.length;
      if (count <= 1) return;
      this.current = ((index % count) + count) % count;

      this.track.style.transform = `translateX(-${this.current * 100}%)`;

      this.slides.forEach((s, i) => {
        s.classList.toggle('is-active', i === this.current);
        s.setAttribute('aria-hidden', i !== this.current);
      });
      this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === this.current));
      this.dots.forEach((d, i)   => d.classList.toggle('is-active', i === this.current));
    },

    /* Jump to slide matching a variant image URL */
    goToVariantImage(imageUrl) {
      if (!imageUrl) return;
      const idx = this.slides.findIndex(slide => {
        const img = slide.querySelector('img');
        return img && img.src.includes(imageUrl.split('?')[0].split('/').pop().split('_')[0]);
      });
      if (idx > -1) this.go(idx);
    },

    onTouchStart(e) { this.startX = e.touches[0].clientX; },
    onTouchEnd(e) {
      const dx = e.changedTouches[0].clientX - this.startX;
      if (Math.abs(dx) > 40) this.go(this.current + (dx < 0 ? 1 : -1));
    },
    onMouseDown(e) { this.isDragging = true; this.startX = e.clientX; },
    onMouseUp(e) {
      if (!this.isDragging) return;
      this.isDragging = false;
      const dx = e.clientX - this.startX;
      if (Math.abs(dx) > 40) this.go(this.current + (dx < 0 ? 1 : -1));
    },
  };

  /* ── VARIANT PICKER ── */
  const VariantPicker = {
    variants: [],
    selectedOptions: [],
    currentVariant: null,

    init() {
      const dataEl = document.getElementById('bb-product-json');
      if (!dataEl) return;
      this.variants = JSON.parse(dataEl.textContent);
      if (!this.variants.length) return;

      /* Seed selected options from the currently selected buttons */
      const groups = document.querySelectorAll('.bb-option-group');
      this.selectedOptions = Array.from(groups).map(group => {
        const selected = group.querySelector('.bb-option-btn.is-selected');
        return selected ? selected.dataset.value : null;
      });

      /* Attach click handlers */
      document.querySelectorAll('.bb-option-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const idx = parseInt(btn.dataset.optionIndex, 10);
          const val = btn.dataset.value;

          /* Update selected state in this group */
          const group = btn.closest('.bb-option-group');
          group.querySelectorAll('.bb-option-btn').forEach(b => {
            b.classList.toggle('is-selected', b === btn);
            b.setAttribute('aria-pressed', b === btn);
          });

          /* Update displayed selected value */
          const label = document.getElementById(`bb-option-val-${idx}`);
          if (label) label.textContent = `— ${val}`;

          this.selectedOptions[idx] = val;
          this.updateVariant();
        });
      });

      this.updateVariant();
    },

    findVariant() {
      return this.variants.find(v => {
        const opts = [v.option1, v.option2, v.option3].filter(Boolean);
        return this.selectedOptions.every((sel, i) => !sel || sel === opts[i]);
      }) || null;
    },

    updateVariant() {
      const variant = this.findVariant();
      if (!variant) return;
      this.currentVariant = variant;

      /* Update hidden input */
      const input = document.getElementById('bb-variant-id');
      if (input) input.value = variant.id;

      /* Update price */
      const priceEl   = document.getElementById('bb-price-current');
      const compareEl = document.getElementById('bb-price-compare');
      if (priceEl) priceEl.textContent = this.formatMoney(variant.price);
      if (compareEl) {
        if (variant.compare_at_price > variant.price) {
          compareEl.textContent = this.formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }

      /* Update ATC button */
      const btn   = document.getElementById('bb-atc-btn');
      const label = document.getElementById('bb-atc-label');
      if (btn && label) {
        if (variant.available) {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
          label.textContent = 'Add to Bag';
        } else {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          label.textContent = 'Sold Out';
        }
      }

      /* Update stock badge */
      const badgeEl = document.getElementById('bb-stock-badge');
      if (variant.inventory_management === 'shopify' && variant.inventory_quantity > 0 && variant.inventory_quantity < 10) {
        if (badgeEl) {
          badgeEl.textContent = `Only ${variant.inventory_quantity} left`;
          badgeEl.style.display = '';
        } else {
          /* Create badge */
          const badges = document.getElementById('bb-product-badges');
          if (badges) {
            const span = document.createElement('span');
            span.id = 'bb-stock-badge';
            span.className = 'bb-badge bb-badge--limited';
            span.textContent = `Only ${variant.inventory_quantity} left`;
            badges.prepend(span);
          }
        }
      } else if (badgeEl) {
        badgeEl.style.display = 'none';
      }

      /* Jump gallery to variant image */
      if (variant.featured_image) {
        Gallery.goToVariantImage(variant.featured_image);
      }
    },

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return `€${amount.replace('.00', '')}`;
    },
  };

  /* ── AJAX CART ── */
  const AjaxCart = {
    init() {
      const form = document.getElementById('bb-product-form');
      if (!form) return;
      form.addEventListener('submit', e => {
        e.preventDefault();
        this.addToCart();
      });
    },

    async addToCart() {
      const btn   = document.getElementById('bb-atc-btn');
      const label = document.getElementById('bb-atc-label');
      const variantId = document.getElementById('bb-variant-id')?.value;
      if (!variantId || !btn || btn.disabled) return;

      /* Loading state */
      btn.classList.add('is-loading');
      const originalText = label.textContent;
      label.textContent = 'Adding';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
        });

        if (!res.ok) throw new Error(res.statusText);
        const data = await res.json();

        /* Success state */
        label.textContent = 'Added ✓';
        setTimeout(() => { label.textContent = originalText; }, 2000);

        /* Dispatch event so Horizon cart drawer opens */
        document.dispatchEvent(new CustomEvent('cart:add', {
          bubbles: true,
          detail: { resource: data },
        }));

      } catch (err) {
        console.error('[BB] Cart add failed:', err);
        label.textContent = 'Error — try again';
        setTimeout(() => { label.textContent = originalText; }, 3000);
      } finally {
        btn.classList.remove('is-loading');
      }
    },
  };

  /* ── INIT ── */
  function init() {
    Gallery.init();
    VariantPicker.init();
    AjaxCart.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.BBProductPage = { Gallery, VariantPicker, AjaxCart };
})();
